import * as React from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  makeStyles,
  IconButton
} from '@material-ui/core';
import StyledCheckbox from '../../../common/checkbox';
import ModalDialog from '../../../modal-dialog/modalDialog';
import useModal from '../../../../hooks/useModal';
import UserTagModalContent from './userTagModalContent';
import Tag from '../../../../models/tag';
import useTag from '../../../../hooks/useTag';
import clsx from 'clsx';
import IconButtonTooltip from '../../../common/iconButtonTooltip';
import { AddCircle } from '@material-ui/icons';
import Client from '../../../../models/client';
import { useFormikContext } from 'formik';
import User from '../../../../models/user';
import SegmentationPermission from '../../../../models/enums/segmentationPermission';

interface Props {
  /** value is the current client */
  value: object;
  checked: object[];
  idField: string;
  labelField: string;
  handleToggle: (value: object) => void;
}

const useStyles = makeStyles({
  listItem: {
    '& .MuiListItemIcon-root': {
      minWidth: '32px'
    }
  },
  loading: {
    color: '#aaa',
    pointerEvents: 'none',
    position: 'relative',
    '& p, & span': {
      color: '#aaa'
    },
    '&::after': {
      content: "''",
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.1)'
    }
  },
  tagButton: {
    color: '#006da8'
  },
  tagButtonLoading: {
    opacity: 0.5
  }
});

/**
 * Transfer list item with extra controls for editing user-specific permissions (tags and fan segmentation)
 */
const ClientListItem = ({ value, checked, idField, labelField, handleToggle }: Props) => {
  const labelId = `transfer-list-item-${value[idField]}-label`;
  const { modalIsOpen, openModal, closeModal } = useModal();
  const { fetchTags } = useTag();
  const [clientTags, setClientTags] = React.useState<Tag[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const classes = useStyles();
  const { values: formikValues } = useFormikContext<User>();

  React.useEffect(() => {
    // retrieve tags for the current client
    fetchTags(value['clientId'])
      .then(tags => {
        if (tags) {
          setClientTags(tags);
        }
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
      });
  }, []);

  const secondaryText = React.useMemo(() => {
    const clientFanSegmentationPermission = formikValues.assignedSegmentation.find(
      fsPermission => fsPermission.clientId === value['clientId']
    );

    let permissions = ''

    switch (clientFanSegmentationPermission.segmentationPermission) {
      case SegmentationPermission.None:
        permissions = '(none)';
        break;
      case SegmentationPermission.Basic:
        permissions = 'Fan Segmentation Cannot Push';
        break;
      case SegmentationPermission.Push:
        permissions = 'Fan Segmentation Can Push';
        break;
      case SegmentationPermission.Power:
        permissions = 'Power User';
        break;
    }
    return `Assigned Permissions: ${permissions}`;
  }, [formikValues.assignedSegmentation]);

  return (
    <React.Fragment>
      <ModalDialog
        isOpen={!loading && modalIsOpen}
        onClose={closeModal}
        title={value[labelField]}
        subtitle="Assign Permissions"
        width="345px"
      >
        <UserTagModalContent clientTags={clientTags} closeModal={closeModal} client={value as Client} />
      </ModalDialog>
      <ListItem
        className={clsx(classes.listItem, loading && classes.loading)}
        role="listitem"
        button
        onClick={() => handleToggle(value)}
      >
        <ListItemIcon>
          <StyledCheckbox
            checked={checked.indexOf(value) !== -1}
            tabIndex={-1}
            disableRipple
            inputProps={{ 'aria-labelledby': labelId }}
          />
        </ListItemIcon>
        <ListItemText id={labelId} primary={value[labelField]} secondary={secondaryText} />
        <ListItemSecondaryAction>
          <IconButtonTooltip title="Assign Permissions">
            {/* span required by tooltip when inner button is disabled */}
            <span>
              <IconButton onClick={openModal} size="small" disabled={loading}>
                <AddCircle className={clsx(classes.tagButton, loading && classes.tagButtonLoading)} />
              </IconButton>
            </span>
          </IconButtonTooltip>
        </ListItemSecondaryAction>
      </ListItem>
    </React.Fragment>
  );
};

export default ClientListItem;
